import {TokenStorage} from '@/api/token.ts';

export const {API_BASE_URL} = process.env;
const STORAGE = new TokenStorage();

export function isLoggedIn(): boolean {
	return STORAGE.getToken() !== null;
}

export function logout() {
	STORAGE.clear();
}

export async function login(username: string, password: string) {
	const response: { token: string } = await request('/auth', {
		method: 'POST',
		body: JSON.stringify({
			username,
			password,
		}),
	});

	STORAGE.update(response.token);
}

export async function activate(password: string, token: string): Promise<void> {
	STORAGE.update(token);

	try {
		await request('/users/me', {
			method: 'PATCH',
			body: JSON.stringify({
				password,
			}),
		});
	} finally {
		STORAGE.update(null);
	}
}

export async function resetPassword(emailAddress: string): Promise<void> {
	await request('/public/users/password-reset', {
		method: 'POST',
		body: JSON.stringify({
			emailAddress,
		}),
	});
}

export async function request<T>(path: string, config?: RequestInit): Promise<T> {
	const headers = new Headers(config?.headers ?? {});
	headers.set('Content-Type', 'application/json');
	headers.set('Accept', 'application/json');

	const token = STORAGE.getToken();

	if (token)
		headers.set('Authorization', 'Bearer ' + token.jwt);

	const response = await fetch(API_BASE_URL + path, {
		...config,
		headers,
	});

	if (!response.ok) {
		let body: object | null = null;

		try {
			body = await response.json();
		} catch {
		}

		if (body) {
			if (isValidationFailedResponse(body))
				throw body;
			else if (isErrorResponse(body))
				throw new Error(body.error.message);
		}

		throw new Error(response.statusText);
	} else if (response.status === 204)
		return null as T;

	return await response.json();
}

interface ErrorResponse<T extends ErrorContent = ErrorContent> {
	error: T,
}

interface ErrorContent<Context extends object = {}> {
	code: string,
	message: string,
	context: Context,
}

export interface ValidationFailed extends ErrorContent<ValidationFailedContext> {
	code: 'validation_failed',
}

export interface ValidationFailedContext {
	failures: ValidationFailures,
}

export interface ValidationFailures {
	[key: string]: ValidationFailure,
}

export interface ValidationFailure {
	code: string,
	path: string,
	message: string,
}

function isErrorResponse(value: any): value is ErrorResponse {
	return typeof value === 'object' && 'error' in value;
}

export function isValidationFailedResponse(value: any): value is ErrorResponse<ValidationFailed> {
	return isErrorResponse(value) && value.error.code === 'validation_failed';
}
